import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import withModal from '../withModalHOC';
import { hideAllModals } from '../../../redux/reducers/modalsSlice';
import { Button } from '@/Components/UI/Button/Button';
import { Select } from '@/Components/UI/Form/Select/Select';
import Modal from '@/Components/UI/Modal/Modal';
import { post } from '../../../Utils/API';
import {
  MODAL_LIVE_QUERY_ID_COLUMN_SELECTION,
  QUERY_PARAM_LIVE_QUERY_ID_COLUMN,
  TOAST_TYPE_ERROR,
} from '../../../Utils/constants';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import ShowToast from '@/Components/Toast/showToastTemplate';
import { openSpreadsheet } from '@/Utils/utils';
import {
  useWebSocketContext,
  WEBSOCKET_CATEGORY_FILE,
} from '@/pages/DatasetPage/WebSocketLibrary/WebSocketContext';
import { setCurrentFile } from '@/redux/reducers/datasetSlice';
import Spinner from '@/Components/UI/Spinner/Spinner';
import { userDataSliceName } from '@/redux/constants';

const LiveQueryIdColumnSelectionModal = () => {
  const dispatch = useDispatch();
  const currentFile = useCurrentFile();

  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const { sendMessageAndWaitForReply } = useWebSocketContext();
  const { user } = useSelector((state) => state[userDataSliceName]);

  useEffect(() => {
    const fetchColumnOptions = async () => {
      const metadataDetails = currentFile?.metadata?.Details || {};
      const sourceType = metadataDetails.type;
      const connectionEndpoint = `connectors/connection/params/${sourceType}`;
      const payload = metadataDetails;
      try {
        const columnOptionResponse = await post(connectionEndpoint, payload);
        if (!columnOptionResponse?.Params) {
          throw new Error('Error fetching column options');
        }
        const uniqueIdColumnParam = columnOptionResponse.Params.find(
          (param) => param.Name === QUERY_PARAM_LIVE_QUERY_ID_COLUMN
        );
        if (!uniqueIdColumnParam) {
          throw new Error('Error fetching column options');
        }
        const choices = uniqueIdColumnParam.Choices.map((choice) => ({
          value: choice.Value,
          label: choice.Label,
        }));
        setOptions(choices);
        setSelectedColumn(uniqueIdColumnParam.Choices[0]?.Value || '');
      } catch (error) {
        console.error('Error fetching column options:', error);
      }
    };

    if (currentFile) {
      fetchColumnOptions();
    }
  }, [currentFile]);

  useEffect(() => {
    const column = options?.find((option) => option?.value === selectedColumn);
    setSelectedColumn(column?.value || '');
  }, [options, selectedColumn]);

  const selectLiveQueryUniqueIdColumnAndLaunch = async () => {
    setIsLoading(true);

    try {
      const metadata = currentFile?.metadata || {};
      const metadataDetails = metadata.Details || {};
      const endpoint = 'external/register';
      const payload = {
        type: metadataDetails.type,
        table: {
          ...metadataDetails.params,
          [QUERY_PARAM_LIVE_QUERY_ID_COLUMN]: selectedColumn,
        },
        parentHandle: metadata.ParentDirectory,
      };
      const response = await post(endpoint, payload);
      if (!response?.Success) {
        ShowToast({
          type: TOAST_TYPE_ERROR,
          text: response?.Message || 'Error registering table',
          errorContext: response,
          endpoint: endpoint,
          payload: payload,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        });
        return;
      }
      sendMessageAndWaitForReply({
        category: WEBSOCKET_CATEGORY_FILE,
        location: response.Handle,
      });
      const updatedFile = {
        ...currentFile,
        metadata: {
          ...metadata,
          FileUuid: response.Handle, // use the true handle
          Details: {
            ...metadataDetails,
            params: {
              ...metadataDetails.params,
              [QUERY_PARAM_LIVE_QUERY_ID_COLUMN]: selectedColumn, // add the selected column to the params
            },
          },
        },
      };
      dispatch(setCurrentFile({ currentFile: updatedFile, user: user }));
      openSpreadsheet(response.Handle, updatedFile.metadata.FileName);
    } catch (e) {
      throw new Error(e);
    } finally {
      setIsLoading(false);
      dispatch(hideAllModals());
    }
  };

  const save = () => selectLiveQueryUniqueIdColumnAndLaunch();

  const cancel = () => {
    setSelectedColumn('');
    setIsLoading(false);
    dispatch(hideAllModals());
  };

  return (
    <Modal
      iconName='link'
      isOpen={true}
      title='Select Unique ID Column'
      articleID='69000861037'
      onClose={cancel}
      size='small'
    >
      <>
        <div className='flex flex-col gap-4 pb-8 border-b border-ui'>
          <Select
            disabled={options.length === 0}
            label='Column'
            onChange={setSelectedColumn}
            options={options.map((option, index) => ({
              ...option,
              key: option.Value || index,
            }))}
            value={selectedColumn}
          />
        </div>

        <div className='flex flex-row justify-end gap-4 mt-4'>
          <Button onClick={cancel}>Cancel</Button>
          <Button disabled={!selectedColumn?.length || isLoading} onClick={save} color='oceanBlue'>
            {isLoading ? <Spinner color='white' size='medium' /> : 'Apply & Open'}
          </Button>
        </div>
      </>
    </Modal>
  );
};

export default withModal({ name: MODAL_LIVE_QUERY_ID_COLUMN_SELECTION })(
  LiveQueryIdColumnSelectionModal
);
